/**
 * Overview block templates.
 *
 * The block never had a renderer: the editor saved items and the public page drew nothing.
 * So there is no legacy arrangement to preserve here — `rows` is the default because it is
 * the one template that renders the block's own relational OverviewItem rows, which is what
 * every existing (invisible) block already holds.
 *
 * The template is stored in OverviewBlock.View; everything a template needs beyond the item
 * rows lives as one JSON blob in OverviewBlock.Data.
 */
export const VIEW_OVERVIEW = {
  /** Live status, the closing time, and a route button. */
  status: 'status',
  /** The block's OverviewItem rows as tappable lines — the default. */
  rows: 'rows',
  /** The full week, today highlighted. */
  week: 'week',
  /** Name plus a scrolling row of action chips. */
  actions: 'actions',
  /** Cover photo, rating, status, two buttons. */
  rating: 'rating',
  /** A paragraph about the place with the status under it. */
  about: 'about',
  /** Popular-times histogram with a live marker. */
  popular: 'popular',
  /** Four labelled facts in a 2×2 grid. */
  facts: 'facts',
  /** Dark card leading with how long is left today. */
  now: 'now',
  /** Static map image, address, copy button, route. */
  map: 'map',
  /** Two locations behind a switch. */
  locations: 'locations',
  /** One-line strips for under a hero. */
  strip: 'strip',
};

/**
 * Content sections a template needs. The Content tab shows exactly the ones the active
 * template renders — a template the user is not on must not put fields in their way.
 */
export const OVERVIEW_SECTIONS = {
  items: 'items',
  schedule: 'schedule',
  place: 'place',
  business: 'business',
  actions: 'actions',
  rating: 'rating',
  about: 'about',
  popular: 'popular',
  facts: 'facts',
  now: 'now',
  locations: 'locations',
  strip: 'strip',
};

/**
 * The three status colours the mockup allows, and no more. `soon` covers both directions —
 * closing soon and opening soon — because to a reader they are the same warning.
 * `unknown` is not a fourth colour: it is the muted ink the block already uses.
 */
export const OVERVIEW_STATUS = {
  open: 'open',
  soon: 'soon',
  closed: 'closed',
  appointment: 'appointment',
  unknown: 'unknown',
};

/** Minutes from a boundary that count as "soon" — the mockup's 30-minute warning. */
export const SOON_THRESHOLD_MINUTES = 30;

/** Weekday keys, Monday first, as stored in the schedule. */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/**
 * Overrides a user can set for the whole block regardless of the weekly schedule.
 * These win over the computed status — a place that is shut for refurbishment is shut
 * whatever its Tuesday hours say.
 */
export const SCHEDULE_MODE = {
  hours: 'hours',
  always: 'always',
  appointment: 'appointment',
  temporarily: 'temporarily',
  permanently: 'permanently',
};
